import { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import { defaultDragManager } from './DragManager.js';

export interface UseDroppableOptions {
  /** Types this zone accepts. Default `[]` (accepts any). */
  accepts?: ReadonlyArray<string>;
  disabled?: boolean;
  /** Fires when a compatible drag enters the zone. */
  onDragEnter?: () => void;
  /** Fires when the active drag leaves the zone. */
  onDragLeave?: () => void;
  /** Fires when a drop is released over this zone. */
  onDrop?: (data: unknown) => void;
}

export interface UseDroppableResult {
  readonly setNode: (el: HTMLElement | null) => void;
  readonly isOver: boolean;
  readonly isActive: boolean;
}

/** Drop target — registers its id + accepted types with the DragManager
 *  and subscribes to state changes so the consumer can highlight a
 *  hovered target or show an "active drop" affordance. Spec 13 §3.2. */
export function useDroppable(options: UseDroppableOptions = {}): UseDroppableResult {
  const { accepts, disabled = false, onDragEnter, onDragLeave, onDrop } = options;
  const id = useId();
  const nodeRef = useRef<HTMLElement | null>(null);
  const [isOver, setIsOver] = useState(false);
  const [isActive, setIsActive] = useState(false);

  // Callback closures and the live `accepts` shape go through a ref so
  // callers passing fresh literals on every render don't churn the manager
  // registration — re-registering mid-drag could silently drop nothing if
  // the hit-test fires during the gap.
  const handlersRef = useRef({ onDragEnter, onDragLeave, onDrop });
  handlersRef.current = { onDragEnter, onDragLeave, onDrop };

  // Stabilise `accepts` by content (sorted + joined) so the registration
  // effect only re-runs when the *set* of accepted types actually changes.
  const acceptsKey = useMemo(
    () => (accepts === undefined ? '' : [...accepts].sort().join(' ')),
    [accepts],
  );
  // Memo keyed on the stable string key so the array identity is stable
  // until the *content* of accepts actually changes — see acceptsKey above.
  const acceptsArr = useMemo<ReadonlyArray<string>>(
    () => (accepts === undefined ? [] : [...accepts]),
    [accepts, acceptsKey],
  );

  const lastOverRef = useRef(false);
  const lastDataRef = useRef<unknown>(undefined);

  const setNode = useCallback((el: HTMLElement | null): void => {
    nodeRef.current = el;
  }, []);

  useEffect(() => {
    if (disabled) return undefined;
    const unregister = defaultDragManager.registerDroppable(id, acceptsArr, () =>
      nodeRef.current === null ? null : nodeRef.current.getBoundingClientRect(),
    );
    const unsub = defaultDragManager.subscribe((state) => {
      if (state === null) {
        if (lastOverRef.current && handlersRef.current.onDrop !== undefined) {
          handlersRef.current.onDrop(lastDataRef.current);
        }
        lastOverRef.current = false;
        lastDataRef.current = undefined;
        setIsOver(false);
        setIsActive(false);
        return;
      }
      const active = acceptsArr.length === 0 || acceptsArr.includes(state.type);
      setIsActive(active);
      const over = state.over === id;
      if (over !== lastOverRef.current) {
        lastOverRef.current = over;
        setIsOver(over);
        if (over) handlersRef.current.onDragEnter?.();
        else handlersRef.current.onDragLeave?.();
      }
      if (over) lastDataRef.current = state.data;
    });
    return () => {
      unsub();
      unregister();
    };
  }, [disabled, id, acceptsArr]);

  return { setNode, isOver, isActive };
}
